import { TOrgTheme, getDevicePref, setDevicePref } from "@eventr/common";
import { ColoursObj, TColourSchemes, getContrastYIQ, setColourScheme } from "@eventr/common-storybook";
import { deviceDetect } from "react-device-detect";
import { create } from "zustand";
import { getDevicePrefName } from "../../utils";

/** Device pref holding the last theme an itinerary resolved to, so a cold start renders it. */
export const EFFECTIVE_THEME_PREF = "effectiveThemePalette";

/** `setDevicePref` types its value as a string, but stores whatever it is handed. */
export const setThemeDevicePref = (key: string, value: unknown, device: string) =>
  setDevicePref(key, value as string, device);

/**
 * The palette only lives in JS (`ColoursObj`), so the browser never learns which theme is
 * showing and draws native UI — notably the classic always-visible macOS scrollbars — light.
 * Tell it via `color-scheme`, and expose a thumb colour for the scrollbar rules in index.css.
 */
const applyColourScheme = (scheme: TColourSchemes, palette?: TOrgTheme["palette"]) => {
  setColourScheme(scheme, palette);
  const background = ColoursObj.backgrounds.primary;
  const isHex = /^#?[0-9a-f]{6}/i.test(background);
  const mode = scheme === "custom" ? (isHex ? getContrastYIQ(background, "light", "dark") : "light") : scheme;
  const root = document.documentElement.style;
  root.colorScheme = mode;
  root.setProperty("--scrollbar-thumb", `color-mix(in srgb, ${ColoursObj.font.primary} 35%, transparent)`);
  root.setProperty("--autofill-background", background);
  root.setProperty("--autofill-text", ColoursObj.font.primary);
};

type TThemeState = {
  selectedColourScheme: TColourSchemes;
  themeOverwrite: boolean;
  customThemeId: string;
  themeImages: Record<string, string>;
  defaultTheme: TOrgTheme | null;
  setScheme: (scheme: TColourSchemes, theme?: TOrgTheme) => void;
  setThemeImages: (images: Record<string, string>) => void;
  getScheme: () => TColourSchemes;
  setOverride: (bool: boolean) => void;
  getOverride: () => boolean;
  setCustomId: (id: string) => void;
  setDefaultTheme: (theme: TOrgTheme) => void;
};

export const useThemeContext = create<TThemeState>()((set, get) => {
  const device = getDevicePrefName(deviceDetect);

  // Get stored preferences
  const themePref = getDevicePref("theme", device, "");
  const themePalette = getDevicePref("themePalette", device, undefined) as TOrgTheme | undefined;
  // What the selected itinerary resolved to last launch. The org config arrives well after
  // first paint, so without this a themed itinerary starts on the wrong colours.
  const lastEffective = getDevicePref(EFFECTIVE_THEME_PREF, device, undefined) as TOrgTheme | undefined;

  let themeImages = themePalette && themePalette.images ? themePalette.images : {};

  // Determine initial scheme
  let initialScheme: TColourSchemes = "light";
  let initialCustomId = "";

  if (themePref === "light" || themePref === "dark") {
    initialScheme = themePref;
  } else if (themePref === "custom" && themePalette) {
    initialScheme = "custom";
    initialCustomId = themePalette._id ?? "";
  } else if (lastEffective) {
    initialScheme = "custom";
    initialCustomId = lastEffective._id ?? "";
    themeImages = lastEffective.images ?? {};
    applyColourScheme("custom", lastEffective.palette);
  } else {
    // Device with no tag theme cached: follow the OS light/dark setting.
    initialScheme = window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  if (initialScheme !== "custom") applyColourScheme(initialScheme);

  return {
    selectedColourScheme: initialScheme,
    themeOverwrite: Boolean(themePref),
    customThemeId: initialCustomId,
    themeImages,
    defaultTheme: null,

    getScheme: () => get().selectedColourScheme,

    setScheme: (scheme, theme) => {
      set(() => ({ selectedColourScheme: scheme }));
      applyColourScheme(scheme, theme?.palette);
    },

    setThemeImages: (images) => {
      set(() => ({ themeImages: images }));
    },

    getOverride: () => get().themeOverwrite,
    setOverride: (bool) => {
      set(() => ({ themeOverwrite: bool }));
    },

    setCustomId: (id) => {
      set(() => ({ customThemeId: id }));
    },

    setDefaultTheme: (theme) => {
      set(() => ({ defaultTheme: theme }));
    },
  };
});
